import { calcularDesvioAtivo } from '@utils/suitability'

/**
 * Find matching recommended asset in a carteira by id_ativo or nome_ativo.
 * @param {Array} ativosCarteira - list of recommended assets in the carteira
 * @param {Object} asset - the asset to match
 * @param {string} assetName - normalized asset name
 * @returns {Object|undefined} matched recommended asset
 */
function findMatchingAtivo(ativosCarteira, asset, assetName) {
  return ativosCarteira.find((a) => {
    if (!a) return false
    if (a.id_ativo && asset.id_ativo && a.id_ativo === asset.id_ativo) return true
    return a.nome_ativo && assetName && a.nome_ativo === assetName
  })
}

/**
 * Check if a carteira's class/subclass matches an asset's class/subclass.
 */
function matchesCarteira(carteira, assetClasse, assetSubclasse) {
  const carteiraClasse = carteira.classe_destino || ''
  const carteiraSubclasse = carteira.subclasse_destino || ''
  return (
    (carteiraClasse && carteiraClasse === assetClasse) ||
    (carteiraSubclasse && carteiraSubclasse === assetSubclasse)
  )
}

/**
 * Get the raw asset value from either 'Aplicação' or 'Aplicacao' fields.
 */
function getAssetValue(asset) {
  return parseFloat(asset['Aplicação'] || asset['Aplicacao']) || 0
}

/**
 * Compute suitability result for an asset matched against a carteira.
 */
function computeDesvio(matchedAtivo, classTotal, asset, carteira) {
  const recommended = matchedAtivo.alocacao_especifica != null
    ? matchedAtivo.alocacao_especifica
    : matchedAtivo.alocacao_global

  const actual = classTotal > 0 ? getAssetValue(asset) / classTotal : null

  const result = calcularDesvioAtivo({
    actual,
    recommended,
    margin: carteira.margem_suitability_ativos,
  })

  if (result.deviation != null) {
    return {
      deviation: result.deviation,
      isOutOfMargin: result.isOutOfMargin,
      margin: carteira.margem_suitability_ativos,
    }
  }
  return null
}

/**
 * Compute suitability deviation for a single asset against recommended portfolios.
 * Pure function — no Vue dependency.
 * @param {Object} params
 * @param {Object} params.asset - asset object from consolidator table
 * @param {string} params.className - class name from the portfolio table grouping
 * @param {Array} params.carteiras - array of recommended portfolios (carteiras recomendadas)
 * @param {number} params.classTotal - total value of all assets in the same class
 * @returns {{ deviation: number, isOutOfMargin: boolean, margin: number }|null}
 */
export function computeSuitabilityForAsset({ asset, className, carteiras, classTotal }) {
  const assetName = asset.nome_ativo || ''
  const assetSubclasse = asset.subclasse || asset.CI || ''
  const assetClasse = asset.classe || className || ''

  for (let c = 0; c < carteiras.length; c++) {
    const carteira = carteiras[c]
    if (!carteira || carteira.margem_suitability_ativos == null) continue
    if (!matchesCarteira(carteira, assetClasse, assetSubclasse)) continue

    const matchedAtivo = findMatchingAtivo(carteira.ativos || [], asset, assetName)
    if (!matchedAtivo) continue

    const result = computeDesvio(matchedAtivo, classTotal, asset, carteira)
    if (result) return result
  }

  return null
}

/**
 * Compute the total value of assets in a class group.
 * @param {Array} assets - array of asset objects
 * @returns {number} total value
 */
export function computeClassTotal(assets) {
  return assets.reduce((sum, a) => sum + getAssetValue(a || {}), 0)
}

/**
 * Process a single class group and populate the suitability map.
 */
function processClassGroup(className, group, carteiras, map) {
  const assets = group.carteira || []
  const classTotal = computeClassTotal(assets)

  for (let j = 0; j < assets.length; j++) {
    const asset = assets[j]
    if (!asset || !asset.id) continue

    const result = computeSuitabilityForAsset({ asset, className, carteiras, classTotal })
    if (result) {
      map[asset.id] = result
    }
  }
}

/**
 * Build a map of asset id -> suitability result for all assets in the portfolio table.
 * Pure function — no Vue dependency.
 * @param {Object} params
 * @param {Array} params.carteirasRecomendadas - recommended portfolios
 * @param {Object} params.carteiraTabela - portfolio table grouped by class name
 * @returns {Object} map { [assetId]: { deviation, isOutOfMargin, margin } }
 */
export function buildSuitabilityMap({ carteirasRecomendadas, carteiraTabela }) {
  const carteiras = carteirasRecomendadas || []
  if (!carteiras.length) return {}

  const table = carteiraTabela || {}
  const classNames = Object.keys(table)
  const map = {}

  for (let i = 0; i < classNames.length; i++) {
    processClassGroup(classNames[i], table[classNames[i]] || {}, carteiras, map)
  }

  return map
}

export const isAllocationLoading = ({ loading, pendingRequests } = {}) =>
  Boolean(loading) || Number(pendingRequests) > 0
